import {
  Autocomplete,
  AutocompleteRenderOptionState,
  Box,
  Chip,
  TextField,
  TextFieldProps
} from '@mui/material';
import type {
  AutocompleteProps,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams
} from '@mui/material/Autocomplete';
import type { SxProps, Theme } from '@mui/material/styles';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import { VirtualizedList } from '@repo/ui';
import React, { forwardRef, HTMLAttributes, ReactNode, useMemo } from 'react';

export type VirtualizedAutocompleteOption = {
  value: string | number;
  label: string;
  icon?: string;
  [key: string]: any;
};

type MuiAutocompleteProps<
  T,
  Multiple extends boolean,
  DisableClearable extends boolean,
  FreeSolo extends boolean
> = AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>;

export interface VirtualizedAutocompleteProps<
  T extends VirtualizedAutocompleteOption,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
> extends Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'ListboxProps'> {
  /**
   * TextField props forwarded to the rendered input
   */
  textFieldProps?: TextFieldProps;

  /**
   * Height of the virtualized list
   */
  listHeight?: number;

  /**
   * Placeholder text applied to the underlying input
   */
  placeholder?: string;

  /**
   * Whether this is a country selector (will show flags)
   */
  isCountrySelector?: boolean;
  /**
   * Function to get the country code from an option
   */
  getCountryCodeFromOption?: (option: T) => string;
}


const GlobalFlag = () => <Box
  component="span"
  sx={{
    width: 24,
    height: 18,
    borderRadius: 0.5,
    mr: 1,
    fontSize: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}
>
  🌍
</Box>

const FlagWrapper = ({ code, height = 18, width = 24 }: { code: string | number, height?: number, width?: number }) => {
  return <>
    {typeof code === 'string' && code !== 'GLOBAL' && (
      <Box
        component="span"
        className={`fi fi-${code.toLowerCase()}`}
        sx={{
          width,
          height,
          borderRadius: 0.5,
          mr: 1,
          display: 'inline-block'
        }}
      />
    )}
    {code === 'GLOBAL' && <GlobalFlag />}
  </>

}

/**
 * Virtualized Autocomplete component that uses react-virtuoso for efficient rendering of large option lists
 */
export const VirtualizedAutocomplete = <
  T extends VirtualizedAutocompleteOption,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
>({
  options,
  getOptionLabel,
  textFieldProps,
  listHeight = 300,
  placeholder,
  renderOption,
  renderTags,
  onChange,
  value,
  multiple,
  freeSolo,
  disableClearable,
  isOptionEqualToValue,
  disabled,
  disableCloseOnSelect,
  isCountrySelector,
  getCountryCodeFromOption,
  slotProps: incomingSlotProps,
  sx: incomingSx,
  ...restProps
}: VirtualizedAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {
  // Create a wrapper component for the virtualized list
  const ListboxComponent = useMemo(() => {
    const Component = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((listboxProps, ref) => {
      const { children, ...rest } = listboxProps;
      const itemData = React.Children.toArray(children) as ReactNode[];

      if (!itemData.length) {
        return <div ref={ref} {...rest}>{children}</div>;
      }

      return (
        <div ref={ref} {...rest}>
          <VirtualizedList
            height={listHeight}
            data={itemData}
            renderItem={(item) => item as ReactNode}
            ListProps={{
              sx: {
                padding: 0,
                '& li': {
                  width: '100%'
                }
              }
            }}
          />
        </div>
      );
    });

    Component.displayName = 'VirtualizedAutocompleteListbox';
    return Component;
  }, [listHeight]);

  // Create custom filter function for enhanced search
  const customFilterOptions = useMemo<
    NonNullable<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['filterOptions']>
  >(() => {
    // Helper function to normalize strings for comparison
    const normalizeString = (str: string) =>
      str.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();

    // Helper function to get first letters of words
    const getFirstLetters = (str: string) => {
      const words = normalizeString(str).split(' ');
      return words.map(word => word.charAt(0)).join('');
    };

    // Return a custom filter function
    return (optionsList: readonly T[], { inputValue }: FilterOptionsState<T>): T[] => {
      if (!inputValue) return [...optionsList];

      const normalizedInput = normalizeString(inputValue);

      return optionsList.filter(option => {
        const label = getOptionLabel ? getOptionLabel(option) : option.label;
        const normalizedLabel = normalizeString(label);
        const firstLetters = getFirstLetters(label);

        // Check for partial match (existing behavior)
        const hasPartialMatch = normalizedLabel.includes(normalizedInput);

        // Check for first-letter match (new behavior)
        const hasFirstLetterMatch = firstLetters.startsWith(normalizedInput);

        return hasPartialMatch || hasFirstLetterMatch;
      });
    };
  }, [getOptionLabel]);

  // Custom renderInput for country fields to show flag in input
  const renderInputWithFlag = useMemo(() => {
    if (!isCountrySelector || multiple) {
      return (params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          {...textFieldProps}
          placeholder={placeholder}
        />
      );
    }

    // Single selection country field - show flag in input
    return (params: AutocompleteRenderInputParams) => {
      const selectedOption = (!Array.isArray(value) ? value : null) as T | null;

      return (
        <TextField
          {...params}
          {...textFieldProps}
          placeholder={placeholder}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: selectedOption ? (
                <FlagWrapper code={getCountryCodeFromOption ? getCountryCodeFromOption(selectedOption) : (selectedOption.value as string)} />
              ) : undefined,
            }
          }}
        />
      );
    };
  }, [isCountrySelector, multiple, textFieldProps, placeholder, value]);

  type SlotProps = MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['slotProps'];
  type PaperSlot = SlotProps extends { paper?: infer R } ? R : never;

  const mergedSlotProps = useMemo<SlotProps>(() => {
    const defaultPaperSx: SxProps<Theme> = (theme) => ({
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'divider',
      boxShadow: 'none',
      borderRadius: 1,
    });

    const mergePaperSx = (existing?: SxProps<Theme>): SxProps<Theme> => {
      if (!existing) {
        return defaultPaperSx;
      }

      if (Array.isArray(existing)) {
        const filtered = existing.filter(
          (item): item is Exclude<typeof item, false | null | undefined> => Boolean(item)
        );
        return [defaultPaperSx, ...filtered] as unknown as SxProps<Theme>;
      }

      return [defaultPaperSx, existing] as unknown as SxProps<Theme>;
    };

    const incomingPaper = incomingSlotProps?.paper;

    const paperSlot: PaperSlot = (() => {
      if (!incomingPaper) {
        return { sx: defaultPaperSx } as PaperSlot;
      }

      if (typeof incomingPaper === 'function') {
        return ((ownerState: Parameters<typeof incomingPaper>[0]) => {
          const resolved = (incomingPaper(ownerState) || {}) as { sx?: SxProps<Theme> } & Record<string, unknown>;
          const sx = mergePaperSx(resolved.sx) as SxProps<Theme>;
          return {
            ...resolved,
            sx
          };
        }) as PaperSlot;
      }

      const resolved = incomingPaper as { sx?: SxProps<Theme> } & Record<string, unknown>;
      const sx = mergePaperSx(resolved.sx) as SxProps<Theme>;

      return {
        ...resolved,
        sx
      } as PaperSlot;
    })();

    return {
      ...incomingSlotProps,
      listbox: {
        ...(incomingSlotProps?.listbox ?? {}),
        component: ListboxComponent
      },
      paper: paperSlot
    } as SlotProps;
  }, [incomingSlotProps, ListboxComponent]);

  const resolvedRenderOption = useMemo<
    MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderOption']
  >(() => {
    if (!isCountrySelector) {
      return renderOption;
    }

    return (listboxProps: HTMLAttributes<HTMLLIElement>, option: T, _state: AutocompleteRenderOptionState) => (
      <li {...listboxProps}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FlagWrapper code={getCountryCodeFromOption ? getCountryCodeFromOption(option) : (option.value as string)} />
          {option.label}
        </Box>
      </li>
    );
  }, [isCountrySelector, renderOption]);

  const resolvedRenderTags = useMemo<
    MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderTags']
  >(() => {
    if (!isCountrySelector || !multiple) {
      return renderTags;
    }

    return (tagValue: T[], getTagProps: AutocompleteRenderGetTagProps, _ownerState: unknown) => (
      tagValue.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={option.value}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FlagWrapper code={getCountryCodeFromOption ? getCountryCodeFromOption(option) : (option.value as string)} height={12} width={16} />
              {option.label}
            </Box>
          }
        />
      ))
    );
  }, [isCountrySelector, multiple, renderTags]);

  const disableCloseOnSelectResolved = multiple ? true : disableCloseOnSelect;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      slotProps={mergedSlotProps}
      filterOptions={customFilterOptions}
      renderInput={renderInputWithFlag}
      renderOption={resolvedRenderOption}
      renderTags={resolvedRenderTags}
      onChange={onChange}
      value={value}
      multiple={multiple}
      freeSolo={freeSolo}
      disableClearable={disableClearable}
      isOptionEqualToValue={isOptionEqualToValue}
      disabled={disabled}
      disableCloseOnSelect={disableCloseOnSelectResolved}
      sx={incomingSx}
      {...restProps}
    />
  );
};

export default VirtualizedAutocomplete; 
